/**
 * AP-9 — Einhängepunkt der Views C (Bus Statistics, Signal Plot). Gleiches
 * Muster wie `mountViewsB`: das Fenster entsteht beim ersten `view:opened`.
 *
 * Die beiden Kadenzen bleiben getrennt wie im Original (FM-TIM-03/04): das
 * 1-s-`stats`-Ereignis füllt Zusammenfassung und Histogramm, das
 * 250-ms-`bus-load`-Ereignis nur die Kurve.
 */

import { createBusStats, type BusStatsHandle } from './BusStats';
import { createSignalPlot, type SignalPlotHandle } from './SignalPlot';
import type { DbcSnapshot } from './signalValue';
import type { EdsSnapshot } from './EdsBrowser';
import { frameCount, readFrameRecord } from '../worker/protocol';
import type { CanConnection } from '../app/connection';
import type { AppStore } from '../app/store';

export function mountViewsC(store: AppStore, connection: CanConnection): void {
  let busStats: BusStatsHandle | null = null;
  let signalPlot: SignalPlotHandle | null = null;

  function ensureBusStats(): BusStatsHandle {
    busStats ??= createBusStats();
    return busStats;
  }

  function ensureSignalPlot(): SignalPlotHandle {
    signalPlot ??= createSignalPlot({
      loadDbcSnapshot: () => connection.getSnapshot('dbc') as Promise<DbcSnapshot>,
      loadEdsSnapshot: () => connection.getSnapshot('eds') as Promise<EdsSnapshot>,
    });
    return signalPlot;
  }

  store.on('view:opened', (event) => {
    if (event.kind === 'bus-stats') ensureBusStats().show();
    else if (event.kind === 'signal-plot') ensureSignalPlot().show();
  });

  connection.on('stats', (event) => busStats?.refresh(event.stats));
  connection.on('bus-load', (event) => busStats?.appendBusLoadSample(event.sample.percent));

  connection.on('rx-batch', (event) => {
    if (signalPlot === null || !signalPlot.panel.visible) return;
    const count = Math.min(event.count, frameCount(event.frames));
    const now = performance.now();
    for (let i = 0; i < count; i += 1) {
      const frame = readFrameRecord(event.frames, i);
      signalPlot.onFrame(frame.id, frame.data, frame.dlc, now);
    }
  });

  // Die Signalliste kennt nur Botschaften aus dem geladenen DBC/EDS.
  store.on('decode:changed', () => {
    if (signalPlot?.panel.visible === true) void signalPlot.refresh();
  });
}
